import { useEffect, useState } from "react";

import { SHEET_Z_BACKDROP, SHEET_Z_PANEL } from "./tokens";

/**
 * Module-level counter — every open ResponsiveSheet claims the next slot so
 * its (backdrop, panel) pair stacks above any sheet that was already open.
 *
 * Without this, two simultaneous sheets share `SHEET_Z_BACKDROP` /
 * `SHEET_Z_PANEL` and the older sheet's panel (z=2999) paints over the newer
 * sheet's backdrop (z=2998) — exactly the bug where the search-filters modal
 * showed through the auth modal's dim layer.
 *
 * Slots are released on unmount so the counter doesn't grow unbounded across
 * a long session. Each slot adds 2 to the base z-index (one for backdrop, one
 * for panel) so the (backdrop, panel) pair stays adjacent.
 */
let openSheetCount = 0;

function claimSlot(): number {
  openSheetCount += 1;
  return openSheetCount;
}

function releaseSlot() {
  openSheetCount = Math.max(0, openSheetCount - 1);
}

/**
 * Returns z-indices for the backdrop + panel of an open sheet. Pass `open`
 * so the slot is claimed only while the sheet is mounted-and-open and
 * released on close (or unmount).
 *
 * Closed sheets (or pre-mount) get the base values — they're not painted, so
 * the actual numbers don't matter; we just keep the hook's render output
 * stable to avoid spurious re-renders.
 */
export function useSheetStackSlot(open: boolean): {
  backdropZ: number;
  panelZ: number;
} {
  const [slot, setSlot] = useState(0);

  useEffect(() => {
    if (!open) return;
    const claimed = claimSlot();
    setSlot(claimed);
    return () => {
      releaseSlot();
      setSlot(0);
    };
  }, [open]);

  const offset = slot > 0 ? (slot - 1) * 2 : 0;
  return {
    backdropZ: SHEET_Z_BACKDROP + offset,
    panelZ: SHEET_Z_PANEL + offset,
  };
}
